import {
	getDisplayImageUrl,
	buildImageProxyUrl,
	canonicalUrlForImageProxy,
	getListingCardImageUrl,
	needsLegacyOurMediaProxy,
	needsOurMediaProxyFallback,
	isSignedMediaUrl,
	LISTING_CARD_PROXY_FORMAT,
} from "@/lib/utils/image-proxy";
import { getPublicMediaUrl } from "@/lib/utils/media-url";

type MediaLike = {
	type?: string | null;
	displayUrl?: string | null;
	url?: string | null;
};

export function getFirstServiceListingImageItem<T extends MediaLike>(media: T[] | undefined | null): T | undefined {
	return (media ?? []).find((item) => {
		if (!item || item.type?.toUpperCase() === "VIDEO") return false;
		return Boolean(item.displayUrl?.trim() || item.url?.trim());
	});
}

export function getServiceMediaRasterSource(item: MediaLike): string | undefined {
	const display = item.displayUrl?.trim();
	if (display) return display;
	const url = item.url?.trim();
	return url || undefined;
}

/** Full gallery asset — `url` first, then `displayUrl` when the original is missing. */
export function getServiceMediaOriginalSource(item: MediaLike): string | undefined {
	const url = item.url?.trim();
	if (url) return url;
	const display = item.displayUrl?.trim();
	return display || undefined;
}

function pushUnique(out: string[], candidate?: string) {
	const c = candidate?.trim();
	if (!c || out.includes(c)) return;
	out.push(c);
}

/** Direct CDN first; image-proxy last so a CloudFront 403 can still recover. */
export function appendGalleryLoadCandidatesForRawUrl(out: string[], rawUrl: string) {
	if (isSignedMediaUrl(rawUrl)) {
		pushUnique(out, rawUrl.trim());
	} else {
		const publicSrc = getPublicMediaUrl(rawUrl) ?? rawUrl;
		pushUnique(out, getDisplayImageUrl(publicSrc));
		pushUnique(out, getDisplayImageUrl(rawUrl));
	}
	if (needsOurMediaProxyFallback(rawUrl) || needsLegacyOurMediaProxy(rawUrl)) {
		pushUnique(out, buildImageProxyUrl(canonicalUrlForImageProxy(rawUrl)));
	}
}

type GalleryLoadCandidatesOptions = {
	listingCard?: boolean;
	/** Service detail carousel — load full `url` before listing `displayUrl`. */
	preferOriginal?: boolean;
};

/** Load candidates for one media item (`displayUrl` then `url`, or reversed when `preferOriginal`). */
export function buildGalleryLoadCandidatesForItem(
	item: MediaLike,
	options?: GalleryLoadCandidatesOptions,
): string[] {
	if (options?.listingCard) {
		const out: string[] = [];
		const primary = getListingCardImageUrl(item.url, item.displayUrl);
		if (primary) pushUnique(out, primary);
		for (const raw of [item.displayUrl, item.url]) {
			if (!raw?.trim()) continue;
			if (needsOurMediaProxyFallback(raw.trim()) || needsLegacyOurMediaProxy(raw.trim())) {
				pushUnique(
					out,
					buildImageProxyUrl(canonicalUrlForImageProxy(raw.trim()), LISTING_CARD_PROXY_FORMAT),
				);
			}
		}
		return out;
	}
	const out: string[] = [];
	const ordered = options?.preferOriginal ? [item.url, item.displayUrl] : [item.displayUrl, item.url];
	for (const raw of ordered) {
		if (!raw?.trim()) continue;
		appendGalleryLoadCandidatesForRawUrl(out, raw.trim());
	}
	return out;
}

/** Primary item candidates, then each subsequent gallery image (for broken S3 / proxy fallbacks). */
export function buildGalleryLoadCandidatesWithSiblingFallbacks(
	media: MediaLike[],
	index: number,
): string[] {
	const out = buildGalleryLoadCandidatesForItem(media[index] ?? {});
	for (let i = index + 1; i < media.length; i++) {
		const item = media[i];
		if (item?.type?.toUpperCase() === "VIDEO") continue;
		const raster = getServiceMediaRasterSource(item);
		if (raster) appendGalleryLoadCandidatesForRawUrl(out, raster);
	}
	return out;
}

function mediaStorageDedupeKey(item: MediaLike): string {
	for (const raw of [item.displayUrl, item.url]) {
		const trimmed = raw?.trim();
		if (!trimmed) continue;
		try {
			return new URL(trimmed.split("?")[0]).pathname.toLowerCase();
		} catch {
			return trimmed.split("?")[0].toLowerCase();
		}
	}
	return "";
}

/** Drop duplicate gallery slides that reference the same stored file. */
export function dedupeServiceMediaForCarousel<T extends MediaLike>(media: T[] | null | undefined): T[] {
	const items = media ?? [];
	if (items.length <= 1) return items;
	const seen = new Set<string>();
	const out: T[] = [];
	for (const item of items) {
		const key = mediaStorageDedupeKey(item);
		if (key && seen.has(key)) continue;
		if (key) seen.add(key);
		out.push(item);
	}
	return out;
}

export function getSubsequentServiceMediaRasterSources(
	media: MediaLike[],
	fromIndex: number,
	options?: Pick<GalleryLoadCandidatesOptions, "preferOriginal">,
): string[] {
	const out: string[] = [];
	const resolveSource = options?.preferOriginal
		? getServiceMediaOriginalSource
		: getServiceMediaRasterSource;
	for (let i = fromIndex + 1; i < media.length; i++) {
		const item = media[i];
		if (item?.type?.toUpperCase() === "VIDEO") continue;
		const raster = resolveSource(item);
		if (raster) out.push(raster);
	}
	return out;
}
